/**
 * Phase 2B · Mobile Quick Sale — POS-style rapid checkout.
 *
 * Second operator surface at `/admin/quick-sale`. Uses the SAME
 * Phase 2 backend as `/admin/new-sale` (POST /api/admin/sticker-orders/manual
 * with `sticker_lists[]` / `product_lines[]` / `custom_lines[]` /
 * `payments[]` / `sale_discount` / `taxability`). Layout is mobile-first
 * with category tiles, one-tap product add, a sticky bottom cart CTA,
 * and a full-screen cart/checkout sheet.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Search, ShoppingCart, X, Plus, Minus, Trash2, User, DollarSign,
  Package, ChevronRight, CheckCircle2, AlertTriangle, Percent, Sparkles,
  Award, Layers, History, Save, FolderOpen, Link2, RotateCcw,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import {
  emptyState, emptyStickerList, buildPayload, computeTotalsPreview,
  extractCartState, hydrateFromCartState, summarizeCartForSave,
} from "./AdminNewSaleHelpers";
import { QuickItemsPickerSheet, quickItemToCustomLine, useQuickItemsCatalog } from "./QuickItemsShared";
import {
  RECENT_LIMIT, pushRecent, readRecentSnapshots,
  shopifyProductSnapshot, quickItemSnapshot,
} from "./AdminQuickSaleRecent";

const PAYMENT_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "zelle", label: "Zelle" },
  { value: "card", label: "Card" },
  { value: "cashapp", label: "Cash App" },
  { value: "wise", label: "WISE" },
  { value: "other", label: "Other" },
];

// Quick Products are pulled live from `db.shopify_products` (Phase 6.1
// mirror) via the same read-only search endpoint the picker uses. We show
// the most-recently-synced products so the operator can one-tap add a
// real product straight to Cart — no intermediate search step. If the
// mirror is empty (Shopify token not synced), the section hides and the
// operator falls back to Categories + Search + Add Custom Sale.
const QUICK_PRODUCT_COUNT = 6;

const CATEGORY_TILES = [
  { key: "stickers",    label: "Single Stickers",   icon: Layers,    action: "single_stickers" },
  { key: "packs",       label: "Sticker Packs & Boxes", icon: Package, action: "search", q: "pack" },
  { key: "albums",      label: "Albums",            icon: Award,     action: "search", q: "album" },
  { key: "sets",        label: "Complete Sets",     icon: Sparkles,  action: "search", q: "set" },
  { key: "certs",       label: "Certificates",      icon: Award,     action: "search", q: "certificate" },
  { key: "quick_items", label: "Quick Items",       icon: Sparkles,  action: "quick_items" },
  { key: "featured",    label: "Featured",          icon: Sparkles,  action: "featured" },
  { key: "custom",      label: "Add Custom Sale",   icon: Plus,      action: "custom" },
  { key: "discount",    label: "Apply Discount",    icon: Percent,   action: "discount" },
];

export default function AdminQuickSale() {
  // Quick Sale is a physical-store POS flow — stamp the business-channel
  // source so reporting routes these orders into Store / Store Saturday
  // instead of the generic "in_person" bucket.
  const [state, setState] = useState(() => emptyState({ keepSource: "quick_sale" }));
  const [view, setView] = useState("home");  // home | search | sticker_choose | sticker | sticker_quick | custom | cart | done
  // null  → "Add new single-sticker group"  (StickerView opens with a fresh
  //         blank draft; on Add-to-Cart the draft is APPENDED).
  // >= 0  → "Edit existing cart line at that index" (StickerView loads that
  //         specific sticker list; on Add-to-Cart it REPLACES that line in-place).
  // Reset to null at every entry point into the sticker flow and after `newSale`.
  const [editingStickerIndex, setEditingStickerIndex] = useState(null);
  const [searchQ, setSearchQ] = useState("");
  const [products, setProducts] = useState([]);
  const [mirrorEmpty, setMirrorEmpty] = useState(false);
  const [quickProducts, setQuickProducts] = useState([]);
  // Quick Sale · Recent (Feb-2026). Scoped to `cu.qs.recent*` — the
  // Historical Entry recent list lives under `cu.hist.recent*`, so
  // historical activity NEVER pollutes the Quick Sale surface.
  const [recentTick, setRecentTick] = useState(0);
  const recentSnapshots = useMemo(
    () => readRecentSnapshots(typeof window === "undefined" ? null : window.localStorage,
                                 RECENT_LIMIT),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recentTick],
  );
  const bumpRecent = () => setRecentTick((n) => n + 1);
  // Shared Quick Items catalog — same collection used by Historical
  // Entry and Sales Orders (`db.historical_quick_items`). Rendered as
  // a preview grid at the bottom of Home view; tapping a tile opens
  // the existing `QuickItemsPickerSheet` (search + line editor).
  const { items: sharedQuickItems, reload: reloadQuickItems } = useQuickItemsCatalog(true);
  // Category-navigation autofocus is intentionally OFF so tapping a
  // category (e.g. "Sticker Packs & Boxes") does NOT open the iOS
  // keyboard on entry. Autofocus is only requested when the operator
  // explicitly taps the top-level Search input.
  const [searchAutoFocus, setSearchAutoFocus] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [completed, setCompleted] = useState(null);
  const [showCustomer, setShowCustomer] = useState(false);
  const [customerQ, setCustomerQ] = useState("");
  const [customerHits, setCustomerHits] = useState([]);
  // Phase 6C · Recent Quick Sales overlay. Opened from the top-bar
  // "RECENT" button; state lives on the Quick Sale page so the cart
  // (`state`) is NEVER unmounted while the sheet is open — closing it
  // simply hides the overlay and we return to the current view with
  // the full cart intact.
  const [recentSalesOpen, setRecentSalesOpen] = useState(false);

  // ── Save Quote & Resume Later ──────────────────────────────────
  // `savedQuoteId` != null → the current cart is already backed by a
  //    saved quote row. Save button updates it in-place; completion
  //    goes through the claim-lock flow.
  // `claimToken`           → set only after a successful claim; cleared
  //    on mark-converted / release / newSale.
  // `completionState`      → "idle" | "claiming" | "submitting" |
  //    "marking" | "ambiguous" | "pre_order_failed".
  //    "ambiguous" + "pre_order_failed" are RECOVERY states — the quote
  //    stays `converting` on the server. "ambiguous" NEVER auto-releases;
  //    only "pre_order_failed" exposes a Release & Retry CTA.
  const [savedQuoteId, setSavedQuoteId] = useState(null);
  const [savedQuoteLabel, setSavedQuoteLabel] = useState("");
  const [claimToken, setClaimToken] = useState(null);
  const [savingQuote, setSavingQuote] = useState(false);
  const [savedQuotesOpen, setSavedQuotesOpen] = useState(false);
  const [completionState, setCompletionState] = useState("idle");
  const [recoveryMessage, setRecoveryMessage] = useState("");

  // ── Product search ─────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    if (view !== "search") return;
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/admin/shopify-products/search", { params: { q: searchQ, limit: 30 } });
        if (!alive) return;
        setProducts(r.data?.results || []);
        setMirrorEmpty(!r.data?.mirror_populated && !searchQ);
      } catch { /* silent */ }
    }, 200);
    return () => { alive = false; clearTimeout(t); };
  }, [searchQ, view]);

  // ── Smart Quick Products (Quick Sale Smart Products Optimization) ──────────────
  // Data-driven top-N tiles from ACTUAL COMPLETED CURRENT SALES.
  // Empty until real sales exist; historical reconstruction sales
  // never appear here.
  const [smartQuickEmpty, setSmartQuickEmpty] = useState(true);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/admin/shopify-products/quick-products", {
          params: { limit: QUICK_PRODUCT_COUNT },
        });
        if (alive) {
          setQuickProducts(r.data?.results || []);
          setSmartQuickEmpty(!!r.data?.empty || !(r.data?.results?.length));
        }
      } catch {
        if (alive) { setQuickProducts([]); setSmartQuickEmpty(true); }
      }
    })();
    return () => { alive = false; };
  }, []);

  // ── Customer search ────────────────────────────────────────────
  useEffect(() => {
    if (!showCustomer || customerQ.length < 2) { setCustomerHits([]); return; }
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/admin/customers/search-master", { params: { q: customerQ, limit: 8 } });
        if (alive) setCustomerHits(r.data?.results || []);
      } catch { /* silent */ }
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [customerQ, showCustomer]);

  // ── Totals ────────────────────────────────────────────────────
  const totals = useMemo(() => computeTotalsPreview(state), [state]);
  const cartCount = (
    state.stickerLists.reduce((a, l) => a + ((l.preview?.snapshot?.lines) || []).reduce((s, ln) => s + ln.qty, 0), 0)
    + state.productLines.reduce((a, p) => a + p.qty, 0)
    + state.customLines.reduce((a, c) => a + c.qty, 0)
  );
  const paidSum = Math.round(state.payments.reduce((a, p) => a + (Number(p.amount) || 0), 0) * 100) / 100;
  const balance = Math.round((totals.total - paidSum) * 100) / 100;
  const isUnderpaid = state.payments.length > 0 && paidSum + 0.005 < totals.total;

  function addProduct(p) {
    setState((s) => {
      // If already present, increment qty.
      const existing = s.productLines.findIndex((l) =>
        (l.shopify_variant_id && l.shopify_variant_id === p.shopify_variant_id)
        || (l.id === p.id)
      );
      if (existing >= 0) {
        const next = [...s.productLines];
        next[existing] = { ...next[existing], qty: next[existing].qty + 1 };
        return { ...s, productLines: next };
      }
      return { ...s, productLines: [...s.productLines, {
        id: p.id, shopify_product_id: p.shopify_product_id,
        shopify_variant_id: p.shopify_variant_id, title: p.title,
        sku: p.sku, handle: p.handle, qty: 1,
        unit_price: p.unit_price, taxable: p.taxable,
      }]};
    });
    // Persist to Recent using canonical Shopify variant identity. Silent
    // no-op when localStorage is unavailable (SSR / private mode).
    const snap = shopifyProductSnapshot(p);
    if (snap && typeof window !== "undefined") {
      pushRecent(window.localStorage, snap); bumpRecent();
    }
    toast.success(`${p.title} added.`, { duration: 1200 });
  }

  async function completeSale() {
    if (saving) return;
    setSaving(true); setSaveError(""); setRecoveryMessage("");
    let localClaim = null;
    try {
      // Step 1 · If this cart is backed by a saved quote, acquire the
      // atomic conversion lock FIRST. Any 409 here = another tab /
      // double-click is already converting — refuse to proceed.
      if (savedQuoteId) {
        setCompletionState("claiming");
        try {
          const cr = await api.post(`/admin/saved-quotes/${savedQuoteId}/claim-conversion`);
          localClaim = cr.data?.claim_token || null;
          setClaimToken(localClaim);
        } catch (err) {
          const detail = err.response?.data?.detail;
          if (err.response?.status === 409) {
            const msg = (detail && typeof detail === "object" && detail.message)
              || "This saved quote is already being completed in another tab/window.";
            setSaveError(msg);
            setCompletionState("idle");
            setSaving(false);
            return;
          }
          throw err;
        }
      }

      // Step 2 · Submit the sale to the EXISTING manual endpoint. We
      // never modify this endpoint — the claim-lock sits ENTIRELY in
      // the saved-quotes layer so legacy Quick Sale flows are untouched.
      setCompletionState("submitting");
      const payload = buildPayload(state);
      let res;
      try {
        res = await api.post("/admin/sticker-orders/manual", payload);
      } catch (err) {
        const status = err.response?.status;
        const detail = err.response?.data?.detail;
        const detailMsg = (detail && typeof detail === "object" && detail.message) || null;
        // Classify the error. Reaching the backend with a validation
        // 400 (incl. underpaid payload-shape errors) PROVES no order
        // was persisted — the server rejected before the write path.
        // Everything else (5xx, 408, network error) is AMBIGUOUS and
        // must leave the quote `converting` for manual reconciliation.
        const confirmedPreOrderFailure = (
          status === 400 || status === 422
          || (status === 409 && detail?.code === "underpaid")
        );
        if (savedQuoteId && localClaim) {
          if (confirmedPreOrderFailure) {
            setCompletionState("pre_order_failed");
            setRecoveryMessage(
              detailMsg
              || formatApiError(detail)
              || "The sale was rejected before any order was created."
            );
          } else {
            setCompletionState("ambiguous");
            setRecoveryMessage(
              "The sale request did not complete cleanly. The saved "
              + "quote is now LOCKED. Verify in Fulfillment whether the "
              + "order was created, then Link it or Release & Retry."
            );
          }
          setSaveError(detailMsg || formatApiError(detail) || err.message || "Save failed.");
          setSaving(false);
          return;
        }
        // Legacy path (no saved quote backing this sale): preserve
        // the exact pre-existing behavior.
        if (detail && typeof detail === "object" && detail.code === "underpaid") {
          setSaveError(detail.message);
        } else {
          setSaveError(formatApiError(detail) || err.message || "Save failed.");
        }
        setCompletionState("idle");
        setSaving(false);
        return;
      }

      // Step 3 · Mark the saved quote converted (if applicable). We
      // await this — any failure here is reported but the sale is
      // already recorded; the admin can reconcile via Link to Existing
      // Order. We DO NOT re-attempt the sale.
      if (savedQuoteId && localClaim) {
        setCompletionState("marking");
        try {
          await api.post(`/admin/saved-quotes/${savedQuoteId}/mark-converted`, {
            claim_token: localClaim,
            order_public_id: res.data.public_id,
          });
        } catch (err) {
          // The sale succeeded but reconciliation failed. Surface a
          // non-fatal warning — the operator sees the sale ID AND is
          // told to reconcile via Saved Quotes recovery.
          toast.error(
            `Sale ${res.data.public_id} completed, but reconciling the saved `
            + `quote failed. Open Saved Quotes → Link to Existing Order to resolve.`,
            { duration: 6000 },
          );
        }
      }

      setCompleted({ public_id: res.data.public_id, snap: res.data.pricing_snapshot });
      setView("done");
      setCompletionState("idle");
      toast.success(`Sale ${res.data.public_id} completed.`);
    } finally {
      setSaving(false);
    }
  }

  // ── Save Quote (create or update in place) ─────────────────────
  async function saveQuote() {
    if (savingQuote) return;
    setSavingQuote(true); setSaveError("");
    try {
      const cart_state = extractCartState(state);
      const { totals_snapshot, customer_ref } = summarizeCartForSave(state, totals);
      // Prefer the customer's display name as the default label when
      // the operator hasn't entered one — makes the Saved Quotes list
      // readable at a glance.
      const label = (savedQuoteLabel || "").trim()
        || (customer_ref.display_name || "")
        || (customer_ref.email || "")
        || `Quote · ${new Date().toLocaleString()}`;
      let doc;
      if (savedQuoteId) {
        const r = await api.patch(`/admin/saved-quotes/${savedQuoteId}`, {
          label, cart_state, totals_snapshot, customer_ref,
        });
        doc = r.data;
        toast.success("Saved quote updated.");
      } else {
        const r = await api.post("/admin/saved-quotes", {
          label, cart_state, totals_snapshot, customer_ref,
        });
        doc = r.data;
        setSavedQuoteId(doc.id);
        toast.success("Quote saved. Resume anytime from Saved Quotes.");
      }
      setSavedQuoteLabel(doc.label || "");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setSaveError(formatApiError(detail) || err.message || "Save failed.");
      toast.error("Could not save quote.");
    } finally {
      setSavingQuote(false);
    }
  }

  function resumeSavedQuote(doc) {
    // Replace the entire cart with the hydrated snapshot. We refuse
    // to merge — the user explicitly asked for versioned snapshots
    // and clean resume semantics.
    setState(hydrateFromCartState(doc.cart_state));
    setSavedQuoteId(doc.id);
    setSavedQuoteLabel(doc.label || "");
    setClaimToken(null);
    setCompletionState("idle");
    setRecoveryMessage("");
    setSaveError(""); setCompleted(null);
    setSavedQuotesOpen(false);
    setView("cart");
  }

  async function linkExistingOrderForCurrent(orderPublicId, reason) {
    if (!savedQuoteId) return;
    try {
      await api.post(`/admin/saved-quotes/${savedQuoteId}/link-existing-order`, {
        order_public_id: orderPublicId,
        reason: reason || "Admin verified the order was created.",
      });
      toast.success(`Linked saved quote to ${orderPublicId}.`);
      // Treat as completed — the sale is already in sticker_orders.
      setCompleted({ public_id: orderPublicId, snap: null });
      setView("done");
      setCompletionState("idle"); setRecoveryMessage("");
      setClaimToken(null);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(formatApiError(detail) || err.message || "Link failed.");
    }
  }

  async function releaseClaimAndRetry(reason) {
    if (!savedQuoteId || !claimToken) return;
    try {
      await api.post(`/admin/saved-quotes/${savedQuoteId}/release-claim`, {
        claim_token: claimToken,
        no_order_created: true,
        reason: reason || "Admin verified no order was created.",
      });
      toast.success("Claim released. You can retry the sale.");
      setClaimToken(null); setCompletionState("idle"); setRecoveryMessage("");
      setSaveError("");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(formatApiError(detail) || err.message || "Release failed.");
    }
  }

  function newSale() {
    setState(emptyState({ keepSource: "quick_sale" }));
    setCompleted(null); setSaveError("");
    setView("home"); setSearchQ(""); setProducts([]);
    setShowCustomer(false); setCustomerQ(""); setCustomerHits([]);
    setEditingStickerIndex(null);
    setSavedQuoteId(null); setSavedQuoteLabel("");
    setClaimToken(null); setCompletionState("idle");
    setRecoveryMessage("");
  }

  // ── Views ──────────────────────────────────────────────────────
  if (view === "done" && completed) {
    return (
      <DoneView
        completed={completed}
        attachedEmail={state.customer?.email || ""}
        onNewSale={newSale}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[var(--paper,_#faf7f2)] pb-24" data-testid="quick-sale-page">
      <RecentSalesSheet
        open={recentSalesOpen}
        onClose={() => setRecentSalesOpen(false)}
      />
      <SavedQuotesSheet
        open={savedQuotesOpen}
        onClose={() => setSavedQuotesOpen(false)}
        onResume={resumeSavedQuote}
        currentId={savedQuoteId}
      />
      {/* Top bar */}
      <div className="sticky top-0 z-30 bg-white border-b-2 border-[var(--ink)] px-3 py-2 flex items-center gap-2" data-testid="quick-sale-top-bar">
        {view !== "home" ? (
          <button onClick={() => setView("home")} className="p-2" data-testid="quick-sale-back-btn">
            <X size={20}/>
          </button>
        ) : (
          <div className="w-9"/>
        )}
        <div className="flex-1">
          <div className="text-[10px] uppercase text-[var(--smoke)]">Admin · POS</div>
          <div className="font-bold">Quick Sale</div>
        </div>
        <button
          onClick={() => setSavedQuotesOpen(true)}
          className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-full border-2 border-[var(--ink)] bg-white active:bg-red-50"
          data-testid="quick-sale-saved-quotes-btn"
          title="Resume a saved quote"
        >
          <FolderOpen size={13}/> Saved
        </button>
        <button
          onClick={() => setRecentSalesOpen(true)}
          className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-full border-2 border-[var(--ink)] bg-white active:bg-red-50"
          data-testid="quick-sale-recent-sales-btn"
          title="Recent completed Quick Sales"
        >
          <History size={13}/> Recent
        </button>
        <div className="text-[11px] px-2 py-1 rounded-full border border-[var(--ink)]" data-testid="quick-sale-mode">
          {state.guest ? "Guest" : (state.customer.first_name || "Customer")}
        </div>
      </div>

      {view === "home" && (
        <HomeView
          onSearch={() => { setSearchAutoFocus(true); setView("search"); }}
          onCategory={(t) => {
            if (t.action === "single_stickers") { setEditingStickerIndex(null); setView("sticker_choose"); }
            else if (t.action === "custom") setView("custom");
            else if (t.action === "discount") setView("cart");
            else if (t.action === "quick_items") setView("quick_items");
            else {
              setSearchQ(t.q || "");
              setSearchAutoFocus(false);  // category browsing must NOT open the iOS keyboard
              setView("search");
            }
          }}
          quickProducts={quickProducts}
          onQuickAdd={addProduct}
          recentSnapshots={recentSnapshots}
          onRecentQuickItem={(snap) => {
            // Route the tap through the shared picker so the line
            // editor (qty / price / taxable / note) is identical to
            // Historical Entry + Sales Orders. Never mutate the cart
            // directly from a Recent tile — the operator must confirm
            // qty + price for Quick Items just like everywhere else.
            setView("quick_items");
          }}
          sharedQuickItems={sharedQuickItems}
          onOpenSharedQuickItems={() => setView("quick_items")}
        />
      )}

      {view === "quick_items" && (
        <QuickItemsPickerSheet
          testidPrefix="quick-sale-quick-items"
          onClose={() => { setView("home"); reloadQuickItems(); }}
          onPick={(item, cfg) => {
            setState((s) => ({
              ...s,
              customLines: [...s.customLines, quickItemToCustomLine(item, cfg)],
            }));
            // Persist Quick Item to Recent so the operator can one-tap it
            // again from the Home view. Keyed by shared catalog id so the
            // Recent tile always points at the SAME db.historical_quick_items
            // row that Historical Entry + Sales Orders see.
            const snap = quickItemSnapshot(item);
            if (snap && typeof window !== "undefined") {
              pushRecent(window.localStorage, snap); bumpRecent();
            }
            toast.success(`${item.name} added.`, { duration: 1200 });
            setView("home");
          }}/>
      )}

      {view === "search" && (
        <SearchView
          q={searchQ} setQ={setSearchQ}
          products={products} mirrorEmpty={mirrorEmpty}
          onPick={addProduct}
          autoFocus={searchAutoFocus}
        />
      )}

      {view === "sticker_choose" && (
        <StickerChooseView
          onDetailed={() => setView("sticker")}
          onQuick={() => setView("sticker_quick")}
        />
      )}

      {view === "sticker" && (
        <StickerView
          state={state}
          setState={setState}
          editingIndex={editingStickerIndex}
          onDone={() => { setEditingStickerIndex(null); setView("home"); }}
        />
      )}

      {view === "sticker_quick" && (
        <QuickStickerView
          state={state}
          setState={setState}
          onDone={() => setView("home")}
        />
      )}

      {view === "custom" && (
        <CustomView state={state} setState={setState} onDone={() => setView("home")} />
      )}

      {view === "cart" && (
        <CartView
          state={state} setState={setState} totals={totals}
          paidSum={paidSum} balance={balance} isUnderpaid={isUnderpaid}
          saving={saving} saveError={saveError}
          showCustomer={showCustomer} setShowCustomer={setShowCustomer}
          customerQ={customerQ} setCustomerQ={setCustomerQ}
          customerHits={customerHits}
          onEditStickerList={(i) => { setEditingStickerIndex(i); setView("sticker"); }}
          onComplete={completeSale}
          savedQuoteId={savedQuoteId}
          savedQuoteLabel={savedQuoteLabel}
          setSavedQuoteLabel={setSavedQuoteLabel}
          savingQuote={savingQuote}
          onSaveQuote={saveQuote}
          completionState={completionState}
          recoveryMessage={recoveryMessage}
          onLinkExistingOrder={linkExistingOrderForCurrent}
          onReleaseAndRetry={releaseClaimAndRetry}
        />
      )}

      {/* Sticky bottom Cart CTA (visible everywhere except cart & done) */}
      {view !== "cart" && view !== "done" && (
        <div className="fixed bottom-0 left-0 right-0 z-40 border-t-2 border-[var(--ink)] bg-white px-3 py-3" data-testid="quick-sale-cart-bar">
          <button
            onClick={() => setView("cart")}
            disabled={cartCount === 0}
            className={`w-full flex items-center justify-between rounded-2xl px-4 py-3 font-bold text-white ${cartCount === 0 ? "bg-gray-400" : "bg-[var(--panini-red,_#dc2626)]"}`}
            data-testid="quick-sale-open-cart-btn"
          >
            <span className="flex items-center gap-2"><ShoppingCart size={18}/> Cart</span>
            <span>{cartCount} items · ${totals.total.toFixed(2)}</span>
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Done view ────────────────────────────────────────────────────
// Owns the local "Send Receipt by Email" sub-flow. The receipt endpoint
// reads from the PERSISTED pricing_snapshot — nothing on this screen
// recomputes totals.
function DoneView({ completed, attachedEmail, onNewSale }) {
  const [showReceipt, setShowReceipt] = useState(false);
  const [email, setEmail] = useState(attachedEmail || "");
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState(null);
  const [err, setErr] = useState("");

  async function send() {
    const to = (email || "").trim();
    if (!to) { setErr("Enter an email address."); return; }
    setSending(true); setErr("");
    try {
      const res = await api.post(
        `/admin/orders/${completed.public_id}/email-receipt`,
        { to },
      );
      setSentTo(res.data?.to || to);
      toast.success(`Receipt sent to ${res.data?.to || to}`);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message || "Send failed.");
    } finally { setSending(false); }
  }

  return (
    <div className="min-h-screen bg-white p-4 flex flex-col items-center justify-center gap-5"
         data-testid="quick-sale-done">
      <CheckCircle2 size={64} className="text-emerald-600"/>
      <h2 className="text-2xl font-bold">Sale Completed</h2>
      <div className="text-xl font-mono" data-testid="quick-sale-done-public-id">
        {completed.public_id}
      </div>
      <div className="text-lg">
        Total: <b data-testid="quick-sale-done-total">
          ${completed.snap?.totals?.total?.toFixed(2)}
        </b>
      </div>

      {!showReceipt && !sentTo && (
        <button
          onClick={() => setShowReceipt(true)}
          className="w-full max-w-xs rounded-2xl border-2 border-[var(--ink)] bg-white py-3 font-semibold"
          data-testid="quick-sale-receipt-open-btn">
          Send Receipt by Email
        </button>
      )}

      {showReceipt && !sentTo && (
        <div className="w-full max-w-xs space-y-2" data-testid="quick-sale-receipt-form">
          <input type="email" value={email}
                 onChange={(e) => setEmail(e.target.value)}
                 placeholder="customer@example.com"
                 className="w-full border-2 border-[var(--ink)] rounded-2xl px-3 py-3 text-base"
                 data-testid="quick-sale-receipt-email"/>
          {err && (
            <div className="text-sm text-red-600" data-testid="quick-sale-receipt-error">{err}</div>
          )}
          <button
            onClick={send} disabled={sending}
            className={`w-full rounded-2xl py-3 font-bold text-white ${sending ? "bg-gray-400" : "bg-[var(--panini-red,_#dc2626)]"}`}
            data-testid="quick-sale-receipt-send-btn">
            {sending ? "Sending…" : "Send Receipt"}
          </button>
        </div>
      )}

      {sentTo && (
        <div className="text-sm text-emerald-700" data-testid="quick-sale-receipt-confirmation">
          Receipt sent to {sentTo}
        </div>
      )}

      <button className="btn btn-primary w-full max-w-xs" onClick={onNewSale}
              data-testid="quick-sale-next">
        New Sale · Next Customer
      </button>
    </div>
  );
}

// ─── Home view ────────────────────────────────────────────────────
function HomeView({ onSearch, onCategory, quickProducts, onQuickAdd,
                        recentSnapshots, onRecentQuickItem,
                        sharedQuickItems, onOpenSharedQuickItems }) {
  const recent = recentSnapshots || [];
  const quickItems = sharedQuickItems || [];
  return (
    <div className="p-3 space-y-4 pb-24" data-testid="quick-sale-home">
      <button onClick={onSearch} className="w-full flex items-center gap-2 border-2 border-[var(--ink)] rounded-2xl px-4 py-3 bg-white" data-testid="quick-sale-search-btn">
        <Search size={18}/> <span className="text-[var(--smoke)]">Search products…</span>
      </button>

      {recent.length > 0 && (
        <div data-testid="quick-sale-recent-section">
          <div className="text-[10px] uppercase text-[var(--smoke)] mb-1">
            Recent
            <span className="ml-1 text-[9px] normal-case text-[var(--smoke)] font-normal">
              (this workspace only)
            </span>
          </div>
          <div className="grid grid-cols-3 gap-2" data-testid="quick-sale-recent-tiles">
            {recent.map((snap, i) => {
              if (snap.kind === "shopify") {
                return (
                  <button
                    key={snap.key}
                    onClick={() => onQuickAdd({
                      id: snap.id,
                      shopify_product_id: snap.shopify_product_id,
                      shopify_variant_id: snap.shopify_variant_id,
                      title: snap.title, unit_price: snap.unit_price,
                      image_url: snap.image_url, taxable: snap.taxable,
                      sku: snap.sku, handle: snap.handle,
                    })}
                    className="border-2 border-[var(--ink)] rounded-xl p-2 bg-white flex flex-col items-start gap-1 min-h-[92px] active:bg-red-50"
                    data-testid={`quick-sale-recent-tile-${i}`}
                  >
                    {snap.image_url ? (
                      <img src={snap.image_url} alt="" className="w-full h-10 object-contain"/>
                    ) : (
                      <div className="w-full h-10 bg-gray-100 rounded flex items-center justify-center text-gray-400">
                        <Package size={16}/>
                      </div>
                    )}
                    <div className="text-[11px] font-semibold leading-tight line-clamp-2 text-left w-full">
                      {snap.title}
                    </div>
                    <div className="text-xs text-[var(--panini-red)] font-bold">
                      ${Number(snap.unit_price || 0).toFixed(2)}
                    </div>
                  </button>
                );
              }
              // Quick Item — route through the shared picker so the
              // operator confirms qty + price like everywhere else.
              return (
                <button
                  key={snap.key}
                  onClick={() => onRecentQuickItem(snap)}
                  className="border-2 border-[var(--ink)] rounded-xl p-2 bg-white flex flex-col items-start gap-1 min-h-[92px] active:bg-red-50"
                  data-testid={`quick-sale-recent-tile-${i}`}
                >
                  <Sparkles size={16} className="text-[var(--panini-red)]"/>
                  <div className="text-[11px] font-semibold leading-tight line-clamp-2 text-left w-full">
                    {snap.name}
                  </div>
                  {snap.default_total != null && (
                    <div className="text-xs text-[var(--panini-red)] font-bold">
                      ${Number(snap.default_total).toFixed(2)}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {quickProducts.length > 0 ? (
        <div>
          <div className="text-[10px] uppercase text-[var(--smoke)] mb-1">
            Quick Products
            <span className="ml-1 text-[9px] normal-case text-[var(--smoke)] font-normal">
              (most-sold)
            </span>
          </div>
          <div className="grid grid-cols-3 gap-2" data-testid="quick-sale-quick-products">
            {quickProducts.map((p, i) => (
              <button
                key={p.id || i}
                onClick={() => onQuickAdd(p)}
                className="border-2 border-[var(--ink)] rounded-xl p-2 bg-white flex flex-col items-start gap-1 min-h-[92px] active:bg-red-50"
                data-testid={`quick-sale-quick-product-${i}`}
              >
                {p.image_url ? (
                  <img src={p.image_url} alt="" className="w-full h-10 object-contain"/>
                ) : (
                  <div className="w-full h-10 bg-gray-100 rounded flex items-center justify-center text-gray-400">
                    <Package size={16}/>
                  </div>
                )}
                <div className="text-[11px] font-semibold leading-tight line-clamp-2 text-left w-full">
                  {p.title}
                </div>
                <div className="text-xs text-[var(--panini-red)] font-bold">
                  ${p.unit_price?.toFixed(2)}
                </div>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div>
          <div className="text-[10px] uppercase text-[var(--smoke)] mb-1">
            Quick Products
            <span className="ml-1 text-[9px] normal-case text-[var(--smoke)] font-normal">
              (most-sold)
            </span>
          </div>
          <div className="text-xs text-[var(--smoke)] p-3 border-2 border-dashed rounded-xl"
               data-testid="quick-sale-quick-products-empty">
            Your most-sold products will appear here as you make sales.
          </div>
        </div>
      )}

      <div>
        <div className="text-[10px] uppercase text-[var(--smoke)] mb-1">Categories</div>
        <div className="grid grid-cols-2 gap-2" data-testid="quick-sale-categories">
          {CATEGORY_TILES.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.key} onClick={() => onCategory(t)}
                      className="border-2 border-[var(--ink)] rounded-2xl p-4 bg-white flex flex-col items-start gap-2 min-h-[92px] active:bg-red-50"
                      data-testid={`quick-sale-cat-${t.key}`}>
                <Icon size={20} className="text-[var(--panini-red)]"/>
                <span className="font-bold text-sm text-left">{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Shared Quick Items catalog — same db.historical_quick_items
          collection used by Historical Entry + Sales Orders. Tapping
          a tile opens the shared picker (search + line editor +
          create-new + Shopify duplicate protection). */}
      <div data-testid="quick-sale-shared-quick-items-section">
        <div className="flex items-baseline justify-between mb-1">
          <div className="text-[10px] uppercase text-[var(--smoke)]">
            Quick Items
            <span className="ml-1 text-[9px] normal-case text-[var(--smoke)] font-normal">
              (shared)
            </span>
          </div>
          <button onClick={onOpenSharedQuickItems}
                  className="text-[10px] uppercase text-[var(--panini-red)] font-bold"
                  data-testid="quick-sale-shared-quick-items-open">
            Manage
          </button>
        </div>
        {quickItems.length === 0 ? (
          <div className="text-xs text-[var(--smoke)] p-3 border-2 border-dashed rounded-xl"
               data-testid="quick-sale-shared-quick-items-empty">
            No Quick Items yet. Tap <b>Manage</b> to add reusable non-Shopify entries
            (Cajita, Jersey, Bucket Hat, …). Shared with Historical Entry and Sales Orders.
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2" data-testid="quick-sale-shared-quick-items-grid">
            {quickItems.slice(0, 9).map((it) => (
              <button key={it.id}
                      onClick={onOpenSharedQuickItems}
                      className="border-2 border-[var(--ink)] rounded-xl p-2 bg-white flex flex-col items-start gap-1 min-h-[80px] active:bg-red-50"
                      data-testid={`quick-sale-shared-quick-item-${it.id}`}>
                <div className="text-[11px] font-semibold leading-tight line-clamp-2 text-left w-full">
                  {it.name}
                </div>
                {it.default_total != null && (
                  <div className="text-xs text-[var(--panini-red)] font-bold">
                    ${Number(it.default_total).toFixed(2)}
                  </div>
                )}
                {!!it.category && (
                  <div className="text-[9px] text-[var(--smoke)] uppercase">
                    {it.category}
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Search view ──────────────────────────────────────────────────
function SearchView({ q, setQ, products, mirrorEmpty, onPick, autoFocus }) {
  return (
    <div className="p-3 space-y-3" data-testid="quick-sale-search">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--smoke)]" size={18}/>
        <input
          value={q} onChange={(e) => setQ(e.target.value)} autoFocus={autoFocus}
          placeholder="title · SKU · handle"
          className="w-full border-2 border-[var(--ink)] rounded-2xl px-10 py-3 text-base"
          data-testid="quick-sale-search-input"
        />
      </div>
      {mirrorEmpty && (
        <div className="text-xs text-[var(--smoke)] flex items-center gap-2 border border-yellow-300 bg-yellow-50 rounded-xl p-2" data-testid="quick-sale-mirror-empty">
          <AlertTriangle size={14}/> Local Shopify mirror is empty in this environment. Use "+ Add Custom Sale" for now.
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        {products.map((p, i) => (
          <button key={p.id || i} onClick={() => onPick(p)}
                  className="border-2 border-[var(--ink)] rounded-2xl p-3 bg-white flex flex-col items-start gap-1 min-h-[128px] active:bg-red-50"
                  data-testid={`quick-sale-product-${i}`}>
            {p.image_url ? (
              <img src={p.image_url} alt="" className="w-full h-16 object-contain"/>
            ) : (
              <div className="w-full h-16 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400">
                <Package size={22}/>
              </div>
            )}
            <div className="text-sm font-semibold leading-tight line-clamp-2 text-left">{p.title}</div>
            <div className="text-xs text-[var(--panini-red)] font-bold">${p.unit_price?.toFixed(2)}</div>
          </button>
        ))}
      </div>
      {products.length === 0 && q && (
        <div className="text-sm text-[var(--smoke)] text-center py-6">No products matched "{q}". Try "+ Add Custom Sale".</div>
      )}
    </div>
  );
}

// ─── Sticker workflow chooser ─────────────────────────────────────
// Historical sales reconstruction: many paper records only carry
// "Single Stickers $10" without codes. This chooser routes to either
// the existing Detailed workflow (paste codes → parse → priced lines)
// or the Quick/Historical workflow (revenue-only, optional quantity).
function StickerChooseView({ onDetailed, onQuick }) {
  return (
    <div className="p-3 space-y-3" data-testid="quick-sale-sticker-choose">
      <div className="text-[10px] uppercase text-[var(--smoke)]">Single Stickers</div>
      <button
        onClick={onDetailed}
        className="w-full border-2 border-[var(--ink)] rounded-2xl p-4 bg-white flex items-start gap-3 active:bg-red-50"
        data-testid="quick-sale-sticker-choose-detailed"
      >
        <Layers size={22} className="text-[var(--panini-red)] mt-0.5"/>
        <div className="text-left">
          <div className="font-bold text-base">Detailed Sticker Sale</div>
          <div className="text-xs text-[var(--smoke)] mt-1">
            Preferred. Paste codes → parse → calculated / editable prices → Add to Cart.
          </div>
        </div>
      </button>
      <button
        onClick={onQuick}
        className="w-full border-2 border-[var(--ink)] rounded-2xl p-4 bg-white flex items-start gap-3 active:bg-red-50"
        data-testid="quick-sale-sticker-choose-quick"
      >
        <DollarSign size={22} className="text-[var(--panini-red)] mt-0.5"/>
        <div className="text-left">
          <div className="font-bold text-base">Quick / Historical Sticker Sale</div>
          <div className="text-xs text-[var(--smoke)] mt-1">
            Revenue only. Use for paper records that lack sticker codes. Quantity may stay unknown.
          </div>
        </div>
      </button>
    </div>
  );
}


// Phase 2B · Quick Sale single-sticker variant taxonomy (owner-approved
// 11-category list, 2026-02-19).  Default is WHITE.  Admin can change
// before completing the sale.  The selected token is persisted directly
// on the generic_sticker custom line and takes precedence over
// Phase 2A title inference at report time.
const QUICK_SALE_SINGLE_STICKER_VARIANTS = [
  { value: "white",            label: "WHITE" },
  { value: "blue",             label: "BLUE" },
  { value: "red",              label: "RED" },
  { value: "purple",           label: "PURPLE" },
  { value: "green",            label: "GREEN" },
  { value: "gold_crumple",     label: "GOLD CRUMPLE" },
  { value: "blue_crumple",     label: "BLUE CRUMPLE" },
  { value: "red_crumple",      label: "RED CRUMPLE" },
  { value: "purple_crumple",   label: "PURPLE CRUMPLE" },
  { value: "green_crumple",    label: "GREEN CRUMPLE" },
  { value: "coca_cola",        label: "COCA COLA" },
];
const QUICK_SALE_DEFAULT_VARIANT = "white";

// ─── Quick / Historical single-sticker entry ─────────────────────
// Emits ONE custom cart line with the metadata block:
//   { kind: "generic_sticker", sticker_quantity_known, sticker_quantity,
//     historical_note }
// Backend preserves these fields verbatim on the persisted
// pricing_snapshot.custom_lines so reporting can compute:
//   • total historical single-sticker revenue
//   • known-quantity sum (only known rows)
//   • transactions-with-unknown-quantity count
// without ever silently coercing an unknown quantity to 1.
const GENERIC_STICKER_PRESETS = [1, 2, 3, 5, 10];

function QuickStickerView({ state, setState, onDone }) {
  const [amount, setAmount] = useState(null);      // number | null
  const [customAmount, setCustomAmount] = useState("");
  const [qtyKnown, setQtyKnown] = useState(false);
  const [qty, setQty] = useState("");              // string in the input
  const [note, setNote] = useState("");
  // Phase 2B · Single-sticker variant selector (default WHITE).  The
  // selected token is persisted verbatim on the emitted generic_sticker
  // custom line so Phase 2A reporting uses it as authoritative stored
  // data (bypassing title inference).
  const [variant, setVariant] = useState(QUICK_SALE_DEFAULT_VARIANT);

  const effectiveAmount = amount != null ? amount : Number(customAmount) || 0;
  // When Historical Sale mode is ON, the entered dollar amount is the
  // FINAL tax-inclusive amount collected on the paper record. Backend
  // reverse-calculates the pre-tax subtotal so subtotal + tax == the
  // entered gross exactly.
  const historicalGross = !!state.isHistorical;

  function add() {
    if (!(effectiveAmount > 0)) return;
    const stickerQty = qtyKnown ? Math.max(1, Math.floor(Number(qty) || 0)) : null;
    if (qtyKnown && !stickerQty) return;  // known-but-missing quantity is invalid
    const line = {
      id: (typeof crypto !== "undefined" && crypto.randomUUID)
          ? crypto.randomUUID() : String(Date.now()),
      description: stickerQty
        ? `Single Stickers — Historical · ${stickerQty} sticker${stickerQty === 1 ? "" : "s"}`
        : "Single Stickers — Historical · Qty unknown",
      qty: 1,
      unit_price: Math.round(effectiveAmount * 100) / 100,
      taxable: true,
      // Preserved verbatim by mixed_sale_pricing.augment_snapshot so
      // reporting can distinguish detailed vs generic sticker sales.
      kind: "generic_sticker",
      sticker_quantity_known: qtyKnown,
      sticker_quantity: stickerQty,
      historical_note: note.trim() || null,
      // Tax-inclusive marker (Historical Mode).
      historical_gross: historicalGross,
      // Phase 2B · Authoritative variant token (owner-selected on this
      // view). Reporting reads this BEFORE falling back to title inference.
      variant,
    };
    setState((s) => ({ ...s, customLines: [...s.customLines, line] }));
    toast.success(historicalGross
      ? `Historical sale added — total collected $${effectiveAmount.toFixed(2)}.`
      : "Generic sticker sale added.");
    onDone();
  }

  return (
    <div className="p-3 space-y-4" data-testid="quick-sale-sticker-quick">
      <div className="text-[10px] uppercase text-[var(--smoke)] flex items-center justify-between">
        <span>Quick / Historical Sticker Sale</span>
        {historicalGross && (
          <span className="text-[10px] font-bold text-[var(--panini-red)]"
                data-testid="quick-sale-generic-gross-badge">
            HISTORICAL · Tax-Inclusive
          </span>
        )}
      </div>

      {/* Total sale amount */}
      <div>
        <div className="text-sm font-semibold mb-1">Total Sale Amount</div>
        <div className="grid grid-cols-3 gap-2" data-testid="quick-sale-generic-amount-presets">
          {GENERIC_STICKER_PRESETS.map((v) => (
            <button
              key={v}
              onClick={() => { setAmount(v); setCustomAmount(""); }}
              className={`border-2 rounded-2xl py-3 font-bold text-lg active:bg-red-50 ${
                amount === v ? "bg-[var(--panini-red,_#dc2626)] text-white border-[var(--panini-red)]"
                              : "border-[var(--ink)] bg-white text-[var(--ink)]"
              }`}
              data-testid={`quick-sale-generic-amount-${v}`}
            >
              ${v}
            </button>
          ))}
          <button
            onClick={() => setAmount(null)}
            className={`border-2 rounded-2xl py-3 font-semibold text-sm ${
              amount === null ? "bg-[var(--panini-red,_#dc2626)] text-white border-[var(--panini-red)]"
                              : "border-[var(--ink)] bg-white text-[var(--ink)]"
            }`}
            data-testid="quick-sale-generic-amount-custom-btn"
          >
            Custom
          </button>
        </div>
        {amount === null && (
          <input
            type="number" min="0" step="0.01" value={customAmount}
            onChange={(e) => setCustomAmount(e.target.value)}
            placeholder="Enter amount"
            className="mt-2 w-full border-2 border-[var(--ink)] rounded-2xl px-3 py-3 text-base"
            data-testid="quick-sale-generic-amount-custom-input"
          />
        )}
      </div>

      {/* Quantity — default UNKNOWN, never silently 1 */}
      <div>
        <div className="text-sm font-semibold mb-1">Sticker Quantity</div>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => { setQtyKnown(false); setQty(""); }}
            className={`border-2 rounded-2xl py-3 font-semibold ${
              !qtyKnown ? "bg-[var(--panini-red,_#dc2626)] text-white border-[var(--panini-red)]"
                        : "border-[var(--ink)] bg-white text-[var(--ink)]"
            }`}
            data-testid="quick-sale-generic-qty-unknown"
          >
            Unknown
          </button>
          <button
            onClick={() => setQtyKnown(true)}
            className={`border-2 rounded-2xl py-3 font-semibold ${
              qtyKnown ? "bg-[var(--panini-red,_#dc2626)] text-white border-[var(--panini-red)]"
                       : "border-[var(--ink)] bg-white text-[var(--ink)]"
            }`}
            data-testid="quick-sale-generic-qty-known"
          >
            Known
          </button>
        </div>
        {qtyKnown && (
          <input
            type="number" min="1" step="1" value={qty}
            onChange={(e) => setQty(e.target.value)}
            placeholder="How many stickers?"
            className="mt-2 w-full border-2 border-[var(--ink)] rounded-2xl px-3 py-3 text-base"
            data-testid="quick-sale-generic-qty-input"
          />
        )}
        <div className="mt-1 text-[11px] text-[var(--smoke)]">
          Unknown quantities stay unknown — never silently counted as 1.
        </div>
      </div>

      {/* Optional note */}
      <div>
        <div className="text-sm font-semibold mb-1">Optional Note</div>
        <input
          value={note} onChange={(e) => setNote(e.target.value)}
          placeholder='e.g. "paper sheet June 18"'
          className="w-full border-2 border-[var(--ink)] rounded-2xl px-3 py-3 text-base"
          data-testid="quick-sale-generic-note"
        />
      </div>

      {/* Phase 2B · Variant selector — default WHITE, 11 owner-approved
          single-sticker variants. Persisted directly on the emitted
          generic_sticker custom line and read as authoritative by
          Phase 2A reporting. */}
      <div>
        <div className="text-sm font-semibold mb-1">Sticker Variant</div>
        <select
          value={variant}
          onChange={(e) => setVariant(e.target.value)}
          className="w-full border-2 border-[var(--ink)] rounded-2xl px-3 py-3 text-base bg-white"
          data-testid="quick-sale-generic-variant"
        >
          {QUICK_SALE_SINGLE_STICKER_VARIANTS.map((v) => (
            <option key={v.value} value={v.value}
                    data-testid={`quick-sale-generic-variant-option-${v.value}`}>
              {v.label}
            </option>
          ))}
        </select>
        <div className="mt-1 text-[11px] text-[var(--smoke)]">
          Default WHITE — change to the exact sticker variant you&rsquo;re selling.
        </div>
      </div>

      <button
        onClick={add}
        disabled={!(effectiveAmount > 0) || (qtyKnown && !(Number(qty) > 0))}
        className={`w-full rounded-2xl py-3 font-bold text-white ${
          effectiveAmount > 0 && (!qtyKnown || Number(qty) > 0)
            ? "bg-[var(--panini-red,_#dc2626)]" : "bg-gray-400"
        }`}
        data-testid="quick-sale-generic-add-btn"
      >
        Add to Cart · ${effectiveAmount.toFixed(2)}
      </button>
    </div>
  );
}


// ─── Sticker view ─────────────────────────────────────────────────
// Quick Sale supports MULTIPLE independent single-sticker groups per
// order — one per variant/parallel. The parent passes `editingIndex`:
//   null  → ADD mode: start with a fresh blank draft list; Add-to-Cart
//           APPENDS the new list (never mutates existing cart lines).
//   >= 0  → EDIT mode: load that specific `state.stickerLists[idx]`;
//           Add-to-Cart REPLACES that line in-place (preserves list id so
//           per-line price overrides keyed by `${list.id}:${code}` carry).
function StickerView({ state, setState, onDone, editingIndex }) {
  const existing = (editingIndex != null && editingIndex >= 0)
    ? state.stickerLists[editingIndex] : null;
  // Stable draft (id survives the whole edit session). A fresh id for ADD
  // mode guarantees it cannot collide with any existing list.
  const [draft] = useState(() => existing ? existing : emptyStickerList("white"));
  const listId = draft.id;
  const [raw, setRaw] = useState(existing?.rawText || "");
  const [variant, setVariant] = useState(existing?.variant || draft.variant);
  const [preview, setPreview] = useState(existing?.preview || null);
  const rawRef = useRef(raw); rawRef.current = raw;

  useEffect(() => {
    if (!raw.trim()) { setPreview(null); return; }
    const t = setTimeout(async () => {
      try {
        // Russia 2018 Black Back short-circuit — this collection uses
        // continuous numeric codes (00, 1..681) with no country prefix,
        // so the WC26 `/orders/parse` + `/orders/quote` round-trip is
        // bypassed in favour of the single `/russia-2018/resolve` call.
        // The response is reshaped into the same {items, snapshot}
        // envelope WC26 produces so NOTHING downstream (cart,
        // computeTotalsPreview, buildPayload) sees a Russia-specific
        // code path from this point on.
        if (variant === "russia_2018") {
          const r = await api.post("/russia-2018/resolve", { text: raw });
          const items = (r.data?.lines || []).map((l) => ({
            code: l.code, qty: l.qty,
          }));
          if (!items.length) {
            if (rawRef.current === raw) {
              setPreview({ items: [], snapshot: { lines: [] } });
            }
            return;
          }
          const snapshotLines = (r.data?.lines || []).map((l) => ({
            code: l.code,
            qty:  l.qty,
            unit_price: l.unit_price_dollars,
            line_total: l.line_total_dollars,
            // Carry Russia identity through the cart so buildPayload
            // can emit the right custom_lines metadata.
            sticker_name:  l.sticker_name,
            country:       l.country,
            country_code:  l.country_code,
            album_slug:    l.album_slug,
            variant:       l.variant,
            category:      l.category,
          }));
          if (rawRef.current === raw) {
            setPreview({ items, snapshot: {
              lines: snapshotLines,
              totals: { tax: { rate: 0 } },
            }});
          }
          return;
        }
        const parsed = await api.post("/orders/parse", {
          album_slug: "panini-fifa-world-cup-2026", raw_text: raw,
        });
        const items = (parsed.data?.items || []).map((i) => ({ code: i.code, qty: i.qty }));
        if (!items.length) { setPreview({ items: [], snapshot: null }); return; }
        const quoted = await api.post("/orders/quote", {
          album_slug: "panini-fifa-world-cup-2026",
          items, delivery: { code: "local_pickup" }, jurisdiction: "US-FL", variant,
        });
        if (rawRef.current === raw) {
          // Normalize the canonical sticker identity label onto each
          // line so the Detailed Sticker Sale table can simply render
          // `ln.sticker_name` without knowing which collection or
          // variant produced the line. First-win fallback chain
          // covers every current + future parser contract:
          //   • WC26 lines carry `display_name` ("Lionel Messi",
          //     "Brazil Badge", "Official Emblem - Part 1")
          //   • Russia 2018 lines carry `sticker_name` (Phase 2)
          //   • `player_name` + `subject` + `name` are legacy fields
          //     some older parser branches still populate.
          const d = quoted.data || {};
          const mapped = {
            ...d,
            lines: (d.lines || []).map((l) => ({
              ...l,
              sticker_name: l.sticker_name
                || l.display_name
                || l.player_name
                || l.subject
                || l.name
                || null,
            })),
          };
          setPreview({ items, snapshot: mapped });
        }
      } catch { /* silent */ }
    }, 300);
    return () => clearTimeout(t);
  }, [raw, variant]);

  function addToCart() {
    if (!preview?.items?.length) return;
    const nextList = { id: listId, variant, rawText: raw, preview };
    setState((s) => {
      // Drop any transient empty placeholder (initial fresh state keeps one
      // empty list for compute safety) — committing a group implicitly
      // discards it. If the committed id already exists (EDIT mode), replace.
      const kept = s.stickerLists.filter((l) => (l.preview?.items?.length || 0) > 0);
      const idx = kept.findIndex((l) => l.id === listId);
      if (idx >= 0) {
        const copy = [...kept];
        copy[idx] = nextList;
        return { ...s, stickerLists: copy };
      }
      return { ...s, stickerLists: [...kept, nextList] };
    });
    onDone();
  }

  const lines = preview?.snapshot?.lines || [];
  // Phase 6E · effective unit price for THIS cart line.
  //
  //   lineOverrides state key = `${listId}:${code}` — authoritative
  //   sale-specific override once the operator edits the Sale $ input.
  //
  //   When present, we display the override everywhere (input value +
  //   line total + parsed-total sum) so what the operator SEES on the
  //   parser is ALWAYS equal to what the cart subtotal (which already
  //   consumes lineOverrides in `computeTotalsPreview`) will charge.
  //
  //   Catalog `ln.unit_price` only initializes a fresh line — once an
  //   override exists, catalog refreshes (via the /orders/quote
  //   re-fetch after a variant switch or text edit) can NEVER
  //   overwrite the operator's intent.
  const effUnit = (code) => {
    const o = state.lineOverrides?.[`${listId}:${code}`];
    return o && Number.isFinite(o.unit_price) ? o.unit_price : null;
  };
  const unitFor = (ln) => {
    const o = effUnit(ln.code);
    return (o == null) ? Number(ln.unit_price) || 0 : o;
  };
  const parsedTotal = lines.reduce(
    (acc, ln) => acc + unitFor(ln) * (Number(ln.qty) || 0), 0);
  return (
    <div className="p-3 space-y-3" data-testid="quick-sale-sticker">
      <div className="flex items-center gap-2">
        <div className="font-bold flex-1">
          Single Stickers
          <span className="ml-2 text-[11px] font-normal text-[var(--smoke)]"
                data-testid="quick-sale-sticker-mode">
            {existing ? "Editing group" : "New group"}
          </span>
        </div>
        <select value={variant} onChange={(e) => setVariant(e.target.value)}
                className="border-2 border-[var(--ink)] rounded-xl px-2 py-2 text-sm"
                data-testid="quick-sale-sticker-variant">
          <option value="white">White</option>
          <option value="blue">Blue</option>
          <option value="gold_crumple">Gold</option>
          <option value="red">Red</option>
          <option value="orange">Orange</option>
          <option value="purple">Purple</option>
          <option value="russia_2018">RUSSIA 2018</option>
        </select>
      </div>
      <textarea rows={4} value={raw} onChange={(e) => setRaw(e.target.value)}
                autoFocus
                placeholder={variant === "russia_2018"
                  ? "117, 146, 185 (numbers only — 00 through 681)"
                  : "BRA-4, ARG-2, POR-13"}
                className="w-full border-2 border-[var(--ink)] rounded-2xl px-3 py-2 text-base"
                data-testid="quick-sale-sticker-paste"/>
      {lines.length > 0 && (
        <table className="w-full text-sm" data-testid="quick-sale-sticker-lines">
          <thead><tr className="text-[10px] uppercase text-[var(--smoke)] text-left">
            <th>Sticker</th>
            <th>Sticker Name</th>
            <th>Qty</th>
            <th>Sale $</th>
            <th className="text-right">Line</th>
          </tr></thead>
          <tbody>
            {lines.map((ln) => {
              const u = unitFor(ln);
              const q = Number(ln.qty) || 0;
              return (
                <tr key={ln.code} className="border-t border-gray-100">
                  <td className="py-1 font-mono">{ln.code}</td>
                  <td className="py-1 text-[var(--ink)]"
                      data-testid={`quick-sale-sticker-name-${ln.code}`}>
                    {ln.sticker_name || <span className="text-[var(--smoke)]">—</span>}
                  </td>
                  <td className="py-1">{q}</td>
                  <td className="py-1">
                    <input type="number" step="0.01" min="0"
                           value={u}
                           onChange={(e) => {
                             // Controlled onChange — every keystroke
                             // persists so the subsequent re-render
                             // (and any upstream /orders/quote
                             // re-fetch) can NEVER wipe the operator's
                             // override. Empty / NaN parses as 0 so
                             // the input never falls back to catalog.
                             const raw_v = e.target.value;
                             const price = raw_v === "" ? 0 : Number(raw_v);
                             if (!Number.isFinite(price)) return;
                             setState((s) => ({ ...s, lineOverrides: {
                               ...s.lineOverrides,
                               [`${listId}:${ln.code}`]: { unit_price: price },
                             }}));
                           }}
                           className="w-20 border rounded px-1 py-0.5"
                           data-testid={`quick-sale-sticker-price-${ln.code}`}/>
                  </td>
                  <td className="py-1 text-right"
                      data-testid={`quick-sale-sticker-line-total-${ln.code}`}>
                    ${(u * q).toFixed(2)}
                  </td>
                </tr>
              );
            })}
          </tbody>
          {/* Phase 6E · live parsed total — sum of CURRENT visible
              Sale $ × qty. Updates on every keystroke so the operator
              can see the group's running total BEFORE adding it to
              cart. Guaranteed equal to the amount that will land on
              the cart subtotal because both use the same override map. */}
          <tfoot>
            <tr className="border-t-2 border-[var(--ink)]">
              <td colSpan={4} className="py-2 text-right font-bold text-[var(--ink)]"
                  data-testid="quick-sale-sticker-parsed-total-label">
                Parsed Total
              </td>
              <td className="py-2 text-right font-bold text-[var(--ink)]"
                  data-testid="quick-sale-sticker-parsed-total">
                ${parsedTotal.toFixed(2)}
              </td>
            </tr>
          </tfoot>
        </table>
      )}
      <button onClick={addToCart} disabled={!lines.length}
              className={`w-full rounded-2xl py-3 font-bold text-white ${lines.length ? "bg-[var(--panini-red,_#dc2626)]" : "bg-gray-400"}`}
              data-testid="quick-sale-sticker-add-btn">
        {existing ? "Save Changes" : "Add to Cart"}
      </button>
      {/* Phase 7 · Copy Sticker Quote — generates a customer-ready
          plain-text quote from the CURRENT parsed table (respects
          edited Sale $ overrides) without adding to cart or
          persisting any state. */}
      <button
        onClick={async () => {
          if (!lines.length) return;
          const text = buildStickerQuoteText({ variant, lines, unitFor });
          const ok = await copyQuoteToClipboard(text);
          if (ok) toast.success("Quote copied to clipboard.");
          else toast.error("Clipboard unavailable — copy manually.");
        }}
        disabled={!lines.length}
        className={`w-full rounded-2xl py-2 font-semibold border-2 ${
          lines.length
            ? "border-[var(--ink)] text-[var(--ink)] bg-white active:bg-neutral-50"
            : "border-gray-300 text-gray-400 bg-gray-50"
        }`}
        data-testid="quick-sale-sticker-copy-quote-btn"
      >
        Copy Sticker Quote
      </button>
    </div>
  );
}

// ─── Custom item view ─────────────────────────────────────────────
function CustomView({ state, setState, onDone }) {
  const [desc, setDesc] = useState("");
  const [qty, setQty] = useState(1);
  const [price, setPrice] = useState("");
  const [taxable, setTaxable] = useState(true);
  function add() {
    if (!desc.trim() || !price) return;
    setState((s) => ({ ...s, customLines: [...s.customLines, {
      id: crypto.randomUUID?.() || String(Date.now()),
      description: desc.trim(), qty: Number(qty) || 1,
      unit_price: Number(price) || 0, taxable,
    }]}));
    toast.success("Custom item added.");
    onDone();
  }
  return (
    <div className="p-3 space-y-3" data-testid="quick-sale-custom">
      <div className="font-bold">Custom Sale</div>
      <input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Description"
             autoFocus className="w-full border-2 border-[var(--ink)] rounded-2xl px-3 py-3"
             data-testid="quick-sale-custom-desc"/>
      <div className="grid grid-cols-2 gap-2">
        <input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)}
               className="border-2 border-[var(--ink)] rounded-2xl px-3 py-3" placeholder="Qty"
               data-testid="quick-sale-custom-qty"/>
        <input type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)}
               className="border-2 border-[var(--ink)] rounded-2xl px-3 py-3" placeholder="Unit price"
               data-testid="quick-sale-custom-price"/>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={taxable} onChange={(e) => setTaxable(e.target.checked)}
               data-testid="quick-sale-custom-taxable"/> Taxable
      </label>
      <button onClick={add} className="w-full bg-[var(--panini-red,_#dc2626)] text-white rounded-2xl py-3 font-bold"
              data-testid="quick-sale-custom-add-btn">Add to Cart</button>
    </div>
  );
}

// ─── Cart / Checkout view ─────────────────────────────────────────
function CartView({
  state, setState, totals, paidSum, balance, isUnderpaid, saving, saveError,
  showCustomer, setShowCustomer, customerQ, setCustomerQ, customerHits,
  onEditStickerList, onComplete,
  savedQuoteId, savedQuoteLabel, setSavedQuoteLabel, savingQuote, onSaveQuote,
  completionState, recoveryMessage,
  onLinkExistingOrder, onReleaseAndRetry,
}) {
  function updateProductQty(i, qty) {
    setState((s) => ({ ...s, productLines: s.productLines.map((l, j) => j === i ? { ...l, qty } : l).filter((l) => l.qty > 0) }));
  }
  function updateCustomQty(i, qty) {
    setState((s) => ({ ...s, customLines: s.customLines.map((l, j) => j === i ? { ...l, qty } : l).filter((l) => l.qty > 0) }));
  }
  function removeProduct(i) {
    setState((s) => ({ ...s, productLines: s.productLines.filter((_, j) => j !== i) }));
  }
  function removeCustom(i) {
    setState((s) => ({ ...s, customLines: s.customLines.filter((_, j) => j !== i) }));
  }
  function removeStickerListAt(idx) {
    setState((s) => {
      const next = s.stickerLists.filter((_, j) => j !== idx);
      // Preserve the compute-safety invariant: always ≥1 entry in stickerLists.
      return { ...s, stickerLists: next.length > 0 ? next : [emptyStickerList()] };
    });
  }
  function payFull(method) {
    setState((s) => ({ ...s, payments: [{ method, amount: totals.total }] }));
  }
  function addPayment() {
    setState((s) => ({ ...s, payments: [...s.payments, { method: "cash", amount: Math.max(0, totals.total - paidSum) }] }));
  }
  function updatePayment(i, patch) {
    setState((s) => ({ ...s, payments: s.payments.map((p, j) => j === i ? { ...p, ...patch } : p) }));
  }
  function removePayment(i) {
    setState((s) => ({ ...s, payments: s.payments.filter((_, j) => j !== i) }));
  }
  function pickCustomer(row) {
    setState((s) => ({ ...s, guest: false, customer: {
      customer_id: row.id || null,
      first_name: row.first_name || "", last_name: row.last_name || "",
      email: row.email || "", phone: row.phone || "",
    }}));
    setShowCustomer(false); setCustomerQ("");
    toast.success("Customer attached.");
  }
  function detachCustomer() {
    setState((s) => ({ ...s, guest: true, customer: { customer_id: null, first_name: "", last_name: "", email: "", phone: "" } }));
  }
  // All populated sticker groups (one entry per variant/parallel the
  // operator has already added to the cart). Each renders as its own
  // cart line with its own Edit + Remove buttons; the index is the
  // position in `state.stickerLists` and is passed to `onEditStickerList`.
  const populatedStickerLists = state.stickerLists
    .map((list, idx) => ({ list, idx }))
    .filter(({ list }) => (list.preview?.snapshot?.lines?.length || 0) > 0);
  const canComplete = totals.total > 0 && !saving && (state.payments.length === 0 || !isUnderpaid)
                      && completionState !== "ambiguous" && completionState !== "pre_order_failed";
  const inRecovery = completionState === "ambiguous" || completionState === "pre_order_failed";
  const canSaveQuote = (
    totals.total > 0
    || (state.stickerLists || []).some((l) => (l.preview?.snapshot?.lines?.length || 0) > 0)
    || (state.productLines || []).length > 0
    || (state.customLines || []).length > 0
  );
  return (
    <div className="p-3 space-y-3 pb-4" data-testid="quick-sale-cart">
      {/* Saved-quote badge + rename field (visible only when backed by a saved row) */}
      {savedQuoteId && (
        <div
          className="flex items-center gap-2 border-2 border-[var(--ink)] rounded-2xl p-3 bg-amber-50"
          data-testid="quick-sale-saved-quote-strip"
        >
          <FolderOpen size={16} className="text-[var(--panini-red)]"/>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase text-[var(--smoke)]">Resumed saved quote</div>
            <input
              type="text"
              value={savedQuoteLabel || ""}
              onChange={(e) => setSavedQuoteLabel(e.target.value)}
              placeholder="Quote label (optional)"
              className="w-full bg-transparent text-sm font-semibold outline-none"
              data-testid="quick-sale-saved-quote-label"
            />
          </div>
        </div>
      )}

      {/* Recovery banner — shown ONLY when a claim is locked and the
          last submission attempt did not confirm a clean outcome. */}
      {savedQuoteId && inRecovery && (
        <RecoveryBanner
          state={completionState}
          message={recoveryMessage}
          onLink={onLinkExistingOrder}
          onRelease={onReleaseAndRetry}
        />
      )}
      {/* Customer strip */}
      <div className="flex items-center gap-2 border-2 border-[var(--ink)] rounded-2xl p-3 bg-white" data-testid="quick-sale-customer">
        <User size={18}/>
        {state.guest ? (
          <>
            <span className="flex-1 text-sm">Guest Sale</span>
            <button onClick={() => setShowCustomer((v) => !v)} className="text-xs underline"
                    data-testid="quick-sale-attach-customer-btn">
              + Attach Customer
            </button>
          </>
        ) : (
          <>
            <span className="flex-1 text-sm font-semibold">
              {state.customer.first_name} {state.customer.last_name}
              <span className="block text-[11px] text-[var(--smoke)] font-normal">{state.customer.email}</span>
            </span>
            <button onClick={detachCustomer} className="text-xs text-red-600"
                    data-testid="quick-sale-detach-customer-btn">Detach</button>
          </>
        )}
      </div>

      {/* Historical Sale mode — reverse-calc tax-inclusive amount for
          Quick/Historical Sticker lines and suppress receipt emails. */}
      <div className="flex items-center gap-2 border-2 border-[var(--ink)] rounded-2xl p-3 bg-white" data-testid="quick-sale-historical-strip">
        <label className="flex items-center gap-2 text-sm font-semibold flex-1">
          <input type="checkbox" checked={!!state.isHistorical}
                 onChange={(e) => setState((s) => ({ ...s, isHistorical: e.target.checked }))}
                 data-testid="quick-sale-historical-toggle"/>
          Historical Sale
        </label>
        {state.isHistorical && (
          <input type="date" value={state.transactionDate || ""}
                 onChange={(e) => setState((s) => ({ ...s, transactionDate: e.target.value }))}
                 className="border rounded-xl px-2 py-1 text-sm"
                 data-testid="quick-sale-historical-date"/>
        )}
      </div>
      {showCustomer && state.guest && (
        <div className="border-2 border-[var(--ink)] rounded-2xl p-3 bg-white" data-testid="quick-sale-customer-search">
          <input value={customerQ} onChange={(e) => setCustomerQ(e.target.value)}
                 placeholder="Name, email, phone, or CU-…"
                 className="w-full border rounded-xl px-3 py-2" autoFocus
                 data-testid="quick-sale-customer-search-input"/>
          {customerHits.length > 0 && (
            <ul className="mt-2 divide-y">
              {customerHits.map((r, i) => (
                <li key={r.id || i}>
                  <button onClick={() => pickCustomer(r)} className="w-full text-left py-2"
                          data-testid={`quick-sale-customer-hit-${i}`}>
                    <div className="text-sm font-semibold">
                      {r.display_name || `${r.first_name || ""} ${r.last_name || ""}`.trim() || r.email}
                    </div>
                    <div className="text-xs text-[var(--smoke)]">{r.email || "—"} · {r.phone || "—"}</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Lines */}
      <div className="border-2 border-[var(--ink)] rounded-2xl bg-white divide-y" data-testid="quick-sale-lines">
        {populatedStickerLists.map(({ list, idx }) => {
          const lines = list.preview.snapshot.lines;
          return (
            <div key={list.id} className="p-3" data-testid={`quick-sale-sticker-group-${idx}`}>
              <div className="flex items-center">
                <div className="font-semibold text-sm flex-1">
                  Stickers · {list.variant.replace("_", " ")}
                </div>
                <button
                  onClick={() => onEditStickerList && onEditStickerList(idx)}
                  className="text-xs underline mr-3 text-[var(--ink)]"
                  data-testid={`quick-sale-edit-stickers-${idx}`}>
                  Edit
                </button>
                <button
                  onClick={() => removeStickerListAt(idx)}
                  className="text-red-600"
                  data-testid={`quick-sale-remove-stickers-${idx}`}>
                  <Trash2 size={14}/>
                </button>
              </div>
              <ul className="mt-1 text-xs text-[var(--smoke)]"
                  data-testid={`quick-sale-sticker-group-items-${idx}`}>
                {lines.map((ln) => {
                  // Phase 6E · cart-line display MUST read the same
                  // override map that `computeTotalsPreview` already
                  // uses, otherwise the per-sticker line totals here
                  // would show the catalog default while the cart
                  // subtotal below shows the override — the exact
                  // inconsistency the operator reported.
                  const ov = state.lineOverrides?.[`${list.id}:${ln.code}`];
                  const u = (ov && Number.isFinite(ov.unit_price))
                    ? ov.unit_price
                    : (Number(ln.unit_price) || 0);
                  const q = Number(ln.qty) || 0;
                  // Phase 7 · Cart lines now include the sticker name
                  // next to the code so the cart matches the parsed
                  // table + copied quote. Falls back to just the code
                  // when metadata is missing.
                  const nameSeg = ln.sticker_name ? ` — ${ln.sticker_name}` : "";
                  return (
                    <li key={ln.code} className="flex justify-between">
                      <span>
                        <span className="font-mono">{ln.code}</span>{nameSeg} × {q}
                      </span>
                      <span data-testid={`quick-sale-cart-sticker-line-total-${idx}-${ln.code}`}>
                        ${(u * q).toFixed(2)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
        {state.productLines.map((p, i) => (
          <LineRow key={i} title={p.title} qty={p.qty} unit={p.unit_price}
                    testid={`quick-sale-product-line-${i}`}
                    onDec={() => updateProductQty(i, p.qty - 1)}
                    onInc={() => updateProductQty(i, p.qty + 1)}
                    onRemove={() => removeProduct(i)}/>
        ))}
        {state.customLines.map((c, i) => (
          <LineRow key={i}
                    title={c.description}
                    qty={c.qty} unit={c.unit_price}
                    subtitle={c.kind === "generic_sticker" ? "Historical · revenue only" : null}
                    testid={`quick-sale-custom-line-${i}`}
                    onDec={() => updateCustomQty(i, c.qty - 1)}
                    onInc={() => updateCustomQty(i, c.qty + 1)}
                    onRemove={() => removeCustom(i)}/>
        ))}
        {populatedStickerLists.length === 0 && state.productLines.length === 0 && state.customLines.length === 0 && (
          <div className="p-4 text-sm text-[var(--smoke)] text-center">Cart is empty.</div>
        )}
      </div>

      {/* Totals */}
      <div className="border-2 border-[var(--ink)] rounded-2xl p-3 bg-white text-sm space-y-1" data-testid="quick-sale-totals">
        <div className="flex justify-between"><span>Subtotal</span><b>${totals.subtotal.toFixed(2)}</b></div>
        <div className="flex justify-between"><span>Discount</span><b>-${totals.discount.toFixed(2)}</b></div>
        <div className="flex justify-between"><span>Tax</span><b>${totals.tax.toFixed(2)}</b></div>
        <div className="flex justify-between text-base pt-1 border-t border-gray-200">
          <span>Total</span>
          <b className="text-[var(--panini-red,_#dc2626)]" data-testid="quick-sale-grand-total">${totals.total.toFixed(2)}</b>
        </div>
      </div>

      {/* Discount */}
      <div className="border-2 border-[var(--ink)] rounded-2xl p-3 bg-white flex items-center gap-2" data-testid="quick-sale-discount-row">
        <Percent size={16} className="text-[var(--panini-red)]"/>
        <select value={state.saleDiscount.kind}
                onChange={(e) => setState((s) => ({ ...s, saleDiscount: { ...s.saleDiscount, kind: e.target.value }}))}
                className="border rounded-xl px-2 py-1 text-sm" data-testid="quick-sale-discount-kind">
          <option value="none">No discount</option>
          <option value="percentage">Percent</option>
          <option value="fixed">Fixed $</option>
        </select>
        {state.saleDiscount.kind !== "none" && (
          <input type="number" min="0" step="0.01" value={state.saleDiscount.value}
                 onChange={(e) => setState((s) => ({ ...s, saleDiscount: { ...s.saleDiscount, value: Number(e.target.value) || 0 }}))}
                 className="w-24 border rounded-xl px-2 py-1 text-sm ml-auto"
                 data-testid="quick-sale-discount-value"/>
        )}
        <label className="ml-auto text-xs flex items-center gap-1">
          <input type="checkbox" checked={state.entireSaleNonTaxable}
                 onChange={(e) => setState((s) => ({ ...s, entireSaleNonTaxable: e.target.checked }))}
                 data-testid="quick-sale-non-taxable"/> Non-tax
        </label>
      </div>

      {/* Payments */}
      <div className="border-2 border-[var(--ink)] rounded-2xl p-3 bg-white space-y-2" data-testid="quick-sale-payments">
        <div className="flex items-center gap-2">
          <DollarSign size={16} className="text-[var(--panini-red)]"/>
          <span className="font-semibold text-sm flex-1">Payment</span>
          <button onClick={addPayment} className="text-xs underline" data-testid="quick-sale-add-payment-btn">+ Add Payment</button>
        </div>
        {state.payments.length === 0 && (
          <div className="grid grid-cols-3 gap-2">
            {PAYMENT_METHODS.slice(0, 6).map((m) => (
              <button key={m.value} onClick={() => payFull(m.value)}
                      className="border-2 border-[var(--ink)] rounded-xl py-2 px-1 text-center leading-tight active:bg-red-50"
                      data-testid={`quick-sale-payfull-${m.value}`}>
                <div className="text-sm font-bold uppercase tracking-wide text-[var(--ink)]">
                  {m.label}
                </div>
                <div className="text-[10px] font-normal text-[var(--smoke)] uppercase tracking-wider mt-0.5">
                  Pay Full
                </div>
              </button>
            ))}
          </div>
        )}
        {state.payments.map((p, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`quick-sale-pay-${i}`}>
            <select value={p.method} onChange={(e) => updatePayment(i, { method: e.target.value })}
                    className="border rounded-xl px-2 py-2 text-sm">
              {PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
            <input type="number" min="0" step="0.01" value={p.amount}
                   onChange={(e) => updatePayment(i, { amount: Number(e.target.value) || 0 })}
                   className="flex-1 border rounded-xl px-2 py-2 text-sm text-right"
                   data-testid={`quick-sale-pay-amount-${i}`}/>
            <button onClick={() => removePayment(i)} className="text-red-600 p-1"><Trash2 size={14}/></button>
          </div>
        ))}
        {state.payments.length > 0 && (
          <div className="text-xs flex gap-3">
            <span>Paid: <b>${paidSum.toFixed(2)}</b></span>
            <span>Balance: <b className={balance > 0 ? "text-red-600" : ""}>${balance.toFixed(2)}</b></span>
          </div>
        )}
        {isUnderpaid && (
          <div className="text-xs text-red-600 flex items-center gap-1" data-testid="quick-sale-underpaid">
            <AlertTriangle size={12}/> Payment is less than the Sale total.
          </div>
        )}
      </div>

      {saveError && (
        <div className="text-sm text-red-600 flex items-center gap-2" data-testid="quick-sale-save-error">
          <AlertTriangle size={14}/> {saveError}
        </div>
      )}


      <button onClick={onComplete} disabled={!canComplete}
              className={`w-full rounded-2xl py-4 font-bold text-white ${canComplete ? "bg-emerald-600" : "bg-gray-400"}`}
              data-testid="quick-sale-complete-btn">
        {saving ? "Completing…" : "Complete Sale"}
      </button>

      {/* Save Quote · Feb-2026. Separate from Complete Sale — this
          persists the current cart as a draft for later resume and
          does NOT create any order, revenue, or inventory movement. */}
      <button
        onClick={onSaveQuote}
        disabled={!canSaveQuote || savingQuote}
        className={`w-full rounded-2xl py-2 font-semibold border-2 flex items-center justify-center gap-2 ${
          canSaveQuote && !savingQuote
            ? "border-[var(--ink)] text-[var(--ink)] bg-white active:bg-neutral-50"
            : "border-gray-300 text-gray-400 bg-gray-50"
        }`}
        data-testid="quick-sale-save-quote-btn"
        title={savedQuoteId ? "Update this saved quote" : "Save this cart as a draft quote"}
      >
        <Save size={14}/>
        {savingQuote
          ? "Saving…"
          : savedQuoteId
            ? "Update Saved Quote"
            : "Save Quote for Later"}
      </button>
      {/* Phase 7 · Copy Full Cart Quote — expands every sticker group,
          lists every product + custom line, and reports the exact
          subtotal / discount / tax / total shown in the on-screen
          Totals card (reuses `totals` from `computeTotalsPreview`).
          Does NOT persist state. */}
      <button
        onClick={async () => {
          const cartHasAnything =
            populatedStickerLists.length > 0
            || state.productLines.length > 0
            || state.customLines.length > 0;
          if (!cartHasAnything) return;
          const text = buildCartQuoteText({ state, totals });
          const ok = await copyQuoteToClipboard(text);
          if (ok) toast.success("Cart quote copied to clipboard.");
          else toast.error("Clipboard unavailable — copy manually.");
        }}
        disabled={totals.total <= 0}
        className={`w-full rounded-2xl py-2 font-semibold border-2 ${
          totals.total > 0
            ? "border-[var(--ink)] text-[var(--ink)] bg-white active:bg-neutral-50"
            : "border-gray-300 text-gray-400 bg-gray-50"
        }`}
        data-testid="quick-sale-cart-copy-quote-btn"
      >
        Copy Full Cart Quote
      </button>
    </div>
  );
}

function LineRow({ title, qty, unit, onDec, onInc, onRemove, testid, subtitle }) {
  return (
    <div className="p-3 flex items-center gap-2" data-testid={testid}>
      <div className="flex-1">
        <div className="text-sm font-semibold line-clamp-1">{title}</div>
        {subtitle
          ? <div className="text-[11px] text-[var(--panini-red)]">{subtitle}</div>
          : <div className="text-xs text-[var(--smoke)]">${unit?.toFixed(2)} each</div>}
      </div>
      <div className="flex items-center border rounded-xl">
        <button onClick={onDec} className="p-2"><Minus size={14}/></button>
        <span className="px-2 text-sm w-8 text-center">{qty}</span>
        <button onClick={onInc} className="p-2"><Plus size={14}/></button>
      </div>
      <div className="text-sm font-bold w-16 text-right">${(qty * unit).toFixed(2)}</div>
      <button onClick={onRemove} className="text-red-600 p-1"><Trash2 size={14}/></button>
    </div>
  );
}

// Phase 7 · Copy Quote helpers — reused by StickerView (parsed table)
// and CartView (full cart). Pure functions, no React state inside.
// Variant→label mapping is deliberately permissive: unknown future
// variants fall back to Title Case so NO code change is needed when a
// new sticker variant / collection is added upstream.
function _collectionLabel(variant) {
  if (variant === "russia_2018") {
    return "Panini FIFA World Cup Russia 2018 — Black Back Stickers";
  }
  const map = {
    white: "White",
    blue: "Blue Parallel",
    gold_crumple: "Gold Crumple",
    red: "Red Parallel",
    orange: "Orange Parallel",
    purple: "Purple Parallel",
  };
  const name = map[variant]
    || String(variant || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  return `Panini FIFA World Cup 2026 — ${name} Stickers`;
}

// Owner directive 2026-02-28 (customer-facing social DM copy).
// Returns the leading emoji for a given sticker variant / collection.
// Additive-only: unknown values fall back to 🏷️. Does NOT alter the
// variant identifier or any catalog logic.
function _collectionEmoji(variant) {
  if (variant === "russia_2018") return "⚫";
  const map = {
    white:        "⚪",
    blue:         "🔵",
    gold_crumple: "🟡",
    red:          "🔴",
    purple:       "🟣",
    green:        "🟢",
    orange:       "🟠",
    black:        "⚫",
  };
  return map[variant] || "🏷️";
}

function _formatQuoteLine(code, name, qty, unit) {
  // Phase 7B · Format: `CODE ×QTY · $LINE — NAME`
  // Keeps prices near the beginning of each line so variable
  // sticker-name lengths never push the dollar amount out of
  // alignment when the quote is pasted into Instagram DM /
  // WhatsApp / Messenger / SMS (owner directive 2026-02-26).
  // For code-less rows (products, custom items) we promote the
  // name to the leading position so the output still reads
  // naturally: `Jersey ×1 · $25.00`.
  const safeName = (name && String(name).trim()) || "";
  const q = Number(qty) || 0;
  const total = (Number(unit) || 0) * q;
  const totalStr = `$${total.toFixed(2)}`;
  if (code) {
    const nameSeg = safeName ? ` — ${safeName}` : "";
    return `${code} ×${q} · ${totalStr}${nameSeg}`;
  }
  return `${safeName || "Item"} ×${q} · ${totalStr}`;
}

function _quoteFooter() {
  return [
    "",
    "Prices subject to availability and final confirmation.",
    "collectorup.com",
  ].join("\n");
}

function buildStickerQuoteText({ variant, lines, unitFor }) {
  // Owner directive 2026-02-28 — customer-facing emoji-rich DM format.
  // Reads naturally on Instagram DM, WhatsApp, Messenger, SMS. The
  // collection header is split on the ` — ` em-dash so the brand
  // (e.g., "Panini FIFA World Cup 2026 —") sits on its own line and
  // the variant sticker name (prefixed with its themed emoji) sits
  // on the following line. `Total Stickers` is computed from the
  // parsed table — never hardcoded.
  const collectionLabel = _collectionLabel(variant);
  const [collectionLeft, ...collectionRight] = collectionLabel.split(" — ");
  const variantSticker = collectionRight.join(" — ") || "";
  const totalStickers = (lines || [])
    .reduce((a, ln) => a + (Number(ln.qty) || 0), 0);
  const header = [
    "⚽ COLLECTORUP.COM ⚽",
    "",
    "📋 STICKER QUOTE",
    "",
    `🏆 ${collectionLeft} —`,
    `${_collectionEmoji(variant)} ${variantSticker}`,
    "",
    `📦 Total Stickers: ${totalStickers}`,
    "",
  ];
  const body = (lines || []).map((ln) => _formatQuoteLine(
    ln.code, ln.sticker_name, Number(ln.qty) || 0, unitFor(ln)
  ));
  const subtotal = (lines || []).reduce(
    (a, ln) => a + (unitFor(ln) * (Number(ln.qty) || 0)), 0);
  const tax = subtotal * 0.07;
  const total = subtotal + tax;
  return [
    ...header, ...body, "",
    "━━━━━━━━━━━━━━",
    `💵 Subtotal: $${subtotal.toFixed(2)}`,
    `🧾 Sales Tax (7%): $${tax.toFixed(2)}`,
    `💰 TOTAL: $${total.toFixed(2)}`,
    "",
    "✅ Ready to order? Reply to confirm!",
    "",
    "📌 Prices subject to availability and final confirmation.",
    "⚽ collectorup.com",
  ].join("\n");
}

function buildCartQuoteText({ state, totals }) {
  const out = ["COLLECTORUP.COM — SALE QUOTE", ""];
  const stickerGroups = (state.stickerLists || [])
    .filter((l) => (l.preview?.snapshot?.lines?.length || 0) > 0);
  stickerGroups.forEach((list) => {
    out.push(_collectionLabel(list.variant));
    const lines = list.preview.snapshot.lines;
    lines.forEach((ln) => {
      const ov = state.lineOverrides?.[`${list.id}:${ln.code}`];
      const unit = (ov && Number.isFinite(ov.unit_price))
        ? ov.unit_price
        : (Number(ln.unit_price) || 0);
      out.push(_formatQuoteLine(ln.code, ln.sticker_name,
                                 Number(ln.qty) || 0, unit));
    });
    out.push("");
  });
  if ((state.productLines || []).length > 0) {
    out.push("Products");
    state.productLines.forEach((p) => {
      const title = p.title || p.name || p.sku || "Item";
      out.push(_formatQuoteLine("", title, Number(p.qty) || 0,
                                 Number(p.unit_price) || 0));
    });
    out.push("");
  }
  if ((state.customLines || []).length > 0) {
    out.push("Custom Items");
    state.customLines.forEach((c) => {
      out.push(_formatQuoteLine("", c.description || "Custom",
                                 Number(c.qty) || 0,
                                 Number(c.unit_price) || 0));
    });
    out.push("");
  }
  out.push(`Subtotal: $${totals.subtotal.toFixed(2)}`);
  if (totals.discount > 0) {
    out.push(`Discount: -$${totals.discount.toFixed(2)}`);
  }
  if (state.entireSaleNonTaxable) {
    out.push("Sales Tax: Non-taxable sale");
  } else {
    out.push(`Sales Tax (7%): $${totals.tax.toFixed(2)}`);
  }
  out.push(`TOTAL: $${totals.total.toFixed(2)}`);
  out.push(_quoteFooter());
  return out.join("\n");
}

async function copyQuoteToClipboard(text) {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_e) { /* fall through */ }
  try {
    const ta = document.createElement("textarea");
    ta.value = text; ta.setAttribute("readonly", "");
    ta.style.position = "fixed"; ta.style.top = "-1000px";
    document.body.appendChild(ta); ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (_e) { return false; }
}


function RecoveryBanner({ state, message, onLink, onRelease }) {
  const [linkId, setLinkId] = useState("");
  const [linkReason, setLinkReason] = useState("");
  const [releaseReason, setReleaseReason] = useState("");
  const isAmbiguous = state === "ambiguous";
  return (
    <div
      className="border-2 border-amber-500 bg-amber-50 rounded-2xl p-3 space-y-3"
      data-testid="quick-sale-recovery-banner"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle size={16} className="text-amber-700 shrink-0 mt-0.5"/>
        <div className="flex-1">
          <div className="text-sm font-bold text-amber-900">
            {isAmbiguous
              ? "Saved quote is LOCKED (converting). Reconcile before retrying."
              : "Sale was rejected before any order was created."}
          </div>
          <div className="text-xs text-amber-900 mt-1">{message}</div>
          {isAmbiguous && (
            <div className="text-[11px] text-amber-900 mt-1">
              This is duplicate-prevention protection, not an exactly-once guarantee —
              verify in Fulfillment first. Never auto-release.
            </div>
          )}
        </div>
      </div>

      {/* Link to Existing Order — always available in recovery */}
      <div className="space-y-1 border-t border-amber-300 pt-2"
            data-testid="quick-sale-recovery-link-section">
        <div className="text-[11px] font-bold uppercase tracking-wider text-amber-900">
          Link to existing order
        </div>
        <input
          type="text"
          value={linkId}
          onChange={(e) => setLinkId(e.target.value)}
          placeholder="GM-26-XXXX"
          className="w-full border-2 border-[var(--ink)] rounded-xl px-3 py-2 text-sm font-mono"
          data-testid="quick-sale-recovery-link-order-id"
        />
        <input
          type="text"
          value={linkReason}
          onChange={(e) => setLinkReason(e.target.value)}
          placeholder="Why are you linking this? (required)"
          className="w-full border-2 border-[var(--ink)] rounded-xl px-3 py-2 text-sm"
          data-testid="quick-sale-recovery-link-reason"
        />
        <button
          onClick={() => onLink(linkId.trim(), linkReason.trim())}
          disabled={!linkId.trim() || linkReason.trim().length < 8}
          className={`w-full rounded-xl py-2 font-bold text-sm flex items-center justify-center gap-2 ${
            linkId.trim() && linkReason.trim().length >= 8
              ? "bg-emerald-600 text-white"
              : "bg-gray-300 text-gray-500"
          }`}
          data-testid="quick-sale-recovery-link-btn"
        >
          <Link2 size={14}/> Link to Existing Order
        </button>
      </div>

      {/* Release & Retry — ONLY exposed when the pre-order failure is
          server-confirmed. Ambiguous outcomes refuse to show this. */}
      {!isAmbiguous && (
        <div className="space-y-1 border-t border-amber-300 pt-2"
              data-testid="quick-sale-recovery-release-section">
          <div className="text-[11px] font-bold uppercase tracking-wider text-amber-900">
            Release & retry (no order was created)
          </div>
          <input
            type="text"
            value={releaseReason}
            onChange={(e) => setReleaseReason(e.target.value)}
            placeholder="Confirm reason (required)"
            className="w-full border-2 border-[var(--ink)] rounded-xl px-3 py-2 text-sm"
            data-testid="quick-sale-recovery-release-reason"
          />
          <button
            onClick={() => onRelease(releaseReason.trim())}
            disabled={releaseReason.trim().length < 8}
            className={`w-full rounded-xl py-2 font-bold text-sm flex items-center justify-center gap-2 ${
              releaseReason.trim().length >= 8
                ? "bg-[var(--ink)] text-white"
                : "bg-gray-300 text-gray-500"
            }`}
            data-testid="quick-sale-recovery-release-btn"
          >
            <RotateCcw size={14}/> Release & Allow Retry
          </button>
        </div>
      )}
    </div>
  );
}

function SavedQuotesSheet({ open, onClose, onResume, currentId }) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");

  async function reload() {
    setLoading(true); setErr("");
    try {
      const r = await api.get("/admin/saved-quotes");
      setRows(r.data?.items || []);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message || "Failed to load saved quotes.");
    } finally { setLoading(false); }
  }

  useEffect(() => {
    if (!open) return;
    reload();
  }, [open]);

  async function openQuote(id) {
    try {
      const r = await api.get(`/admin/saved-quotes/${id}`);
      onResume(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message || "Could not open quote.");
    }
  }

  async function abandon(id) {
    if (!window.confirm("Abandon this draft? The cart snapshot will be hidden from the list.")) return;
    try {
      await api.delete(`/admin/saved-quotes/${id}`);
      toast.success("Quote abandoned.");
      await reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message || "Abandon failed.");
    }
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center"
      onClick={onClose}
      data-testid="quick-sale-saved-quotes-sheet"
    >
      <div
        className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl border-2 border-[var(--ink)] max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b-2 border-[var(--ink)] flex items-center gap-2 sticky top-0 bg-white rounded-t-2xl">
          <FolderOpen size={18} className="text-[var(--panini-red)]"/>
          <div className="flex-1">
            <div className="text-[10px] uppercase text-[var(--smoke)] tracking-wider">Admin · POS</div>
            <div className="font-bold text-base">Saved Quotes</div>
          </div>
          <button onClick={onClose} className="p-2 -mr-2"
                  data-testid="quick-sale-saved-quotes-close">
            <X size={20}/>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="p-8 text-center text-sm text-[var(--smoke)]"
                 data-testid="quick-sale-saved-quotes-loading">
              Loading saved quotes…
            </div>
          )}
          {err && !loading && (
            <div className="p-6 text-sm text-red-600 flex items-start gap-2"
                 data-testid="quick-sale-saved-quotes-error">
              <AlertTriangle size={16} className="shrink-0 mt-0.5"/>
              <span>{err}</span>
            </div>
          )}
          {!loading && !err && rows.length === 0 && (
            <div className="p-10 text-center text-sm text-[var(--smoke)]"
                 data-testid="quick-sale-saved-quotes-empty">
              No saved quotes yet. Build a cart and tap "Save Quote for Later" to add one here.
            </div>
          )}
          {!loading && !err && rows.length > 0 && (
            <ul className="divide-y divide-gray-200" data-testid="quick-sale-saved-quotes-list">
              {rows.map((row, i) => {
                const isCurrent = row.id === currentId;
                const isConverting = row.status === "converting";
                return (
                  <li key={row.id || i} className={isCurrent ? "bg-amber-50" : ""}>
                    <div className="px-4 py-3 flex items-start gap-3"
                         data-testid={`quick-sale-saved-quotes-row-${i}`}>
                      <button
                        onClick={() => openQuote(row.id)}
                        className="flex-1 text-left"
                        data-testid={`quick-sale-saved-quotes-resume-${i}`}
                      >
                        <div className="font-semibold text-sm text-[var(--ink)] truncate">
                          {row.label || row.customer_display_name || "(Untitled quote)"}
                        </div>
                        <div className="text-[12px] text-[var(--smoke)] mt-0.5">
                          {row.customer_display_name || "Guest"}
                          {row.customer_email ? ` · ${row.customer_email}` : ""}
                        </div>
                        <div className="text-sm font-bold text-[var(--panini-red)] mt-1">
                          ${Number(row.total || 0).toFixed(2)}
                          <span className="font-normal text-[var(--smoke)] text-xs ml-1">
                            · {row.item_count || 0} items
                          </span>
                        </div>
                        <div className="text-[11px] mt-0.5">
                          <span className={`inline-block px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wider font-bold ${
                            isConverting
                              ? "bg-amber-100 text-amber-800 border-amber-300"
                              : "bg-emerald-50 text-emerald-700 border-emerald-200"
                          }`}>
                            {isConverting ? "Converting · locked" : row.status}
                          </span>
                        </div>
                      </button>
                      {row.status === "draft" && (
                        <button
                          onClick={() => abandon(row.id)}
                          className="text-red-600 p-2"
                          title="Abandon this draft"
                          data-testid={`quick-sale-saved-quotes-abandon-${i}`}
                        >
                          <Trash2 size={14}/>
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="px-4 py-2 text-[10px] text-[var(--smoke)] border-t border-gray-200 text-center">
          Saved quotes are drafts only — they do not create a sale or affect revenue, inventory, or the Sales Dashboard.
        </div>
      </div>
    </div>
  );
}

/* ─── Phase 6C · Recent Quick Sales overlay ──────────────────────────
 *
 * Thin, read-only tray fed by `GET /api/admin/orders/quick-sales/recent`.
 * Rendered as a bottom-sheet-style overlay OVER the Quick Sale page —
 * the page itself (and its cart state) is never unmounted, so closing
 * the sheet returns to the exact in-progress sale.
 *
 * Clicking a row opens the existing admin detail view
 * (`/admin/fulfillment/{public_id}`) in a NEW TAB so the operator can
 * glance at a prior sale without losing their current cart. The sheet
 * is pure UI — zero mutations, zero cart writes.
 */
function _formatSaleWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = (d.toDateString() === now.toDateString());
    const t = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    if (sameDay) return `Today · ${t}`;
    const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return `Yesterday · ${t}`;
    return `${d.toLocaleDateString([], { month: "short", day: "numeric" })} · ${t}`;
  } catch { return iso; }
}

function _payLabel(key) {
  if (!key) return "—";
  const m = { cash: "Cash", zelle: "Zelle", card: "Card",
              cashapp: "Cash App", wise: "WISE", other: "Other" };
  return m[key] || key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

function _statusLabel(s) {
  if (!s) return "";
  return s.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

function RecentSalesSheet({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true); setErr("");
    (async () => {
      try {
        const r = await api.get("/admin/orders/quick-sales/recent", {
          params: { limit: 20 },
        });
        if (!alive) return;
        setRows(r.data?.items || []);
      } catch (e) {
        if (!alive) return;
        setErr(formatApiError(e.response?.data?.detail) || e.message || "Failed to load recent sales.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open]);

  if (!open) return null;

  const openOrder = (publicId) => {
    // Open in a new tab so the Quick Sale cart stays mounted + intact.
    window.open(`/admin/fulfillment/${encodeURIComponent(publicId)}`,
                 "_blank", "noopener");
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center"
      onClick={onClose}
      data-testid="quick-sale-recent-sales-sheet"
    >
      <div
        className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl border-2 border-[var(--ink)] max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b-2 border-[var(--ink)] flex items-center gap-2 sticky top-0 bg-white rounded-t-2xl">
          <History size={18} className="text-[var(--panini-red)]"/>
          <div className="flex-1">
            <div className="text-[10px] uppercase text-[var(--smoke)] tracking-wider">Admin · POS</div>
            <div className="font-bold text-base">Recent Quick Sales</div>
          </div>
          <button onClick={onClose} className="p-2 -mr-2"
                  data-testid="quick-sale-recent-sales-close">
            <X size={20}/>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="p-8 text-center text-sm text-[var(--smoke)]"
                 data-testid="quick-sale-recent-sales-loading">
              Loading recent sales…
            </div>
          )}
          {err && !loading && (
            <div className="p-6 text-sm text-red-600 flex items-start gap-2"
                 data-testid="quick-sale-recent-sales-error">
              <AlertTriangle size={16} className="shrink-0 mt-0.5"/>
              <span>{err}</span>
            </div>
          )}
          {!loading && !err && rows.length === 0 && (
            <div className="p-10 text-center text-sm text-[var(--smoke)]"
                 data-testid="quick-sale-recent-sales-empty">
              No recent Quick Sales yet. Completed sales will show up here.
            </div>
          )}
          {!loading && !err && rows.length > 0 && (
            <ul className="divide-y divide-gray-200" data-testid="quick-sale-recent-sales-list">
              {rows.map((row, i) => {
                const total = typeof row.total === "number"
                  ? `$${row.total.toFixed(2)}` : "—";
                return (
                  <li key={row.public_id || i}>
                    <button
                      onClick={() => openOrder(row.public_id)}
                      className="w-full text-left px-4 py-3 active:bg-red-50 flex items-start gap-3"
                      data-testid={`quick-sale-recent-sales-row-${i}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-mono font-bold text-sm text-[var(--ink)] truncate">
                          {row.public_id}
                        </div>
                        <div className="text-[12px] text-[var(--smoke)] mt-0.5">
                          {_formatSaleWhen(row.submitted_at)}
                        </div>
                        <div className="text-sm font-semibold text-[var(--ink)] mt-1">
                          {total}
                          <span className="font-normal text-[var(--smoke)]"> · {_payLabel(row.payment_method)}</span>
                        </div>
                        <div className="text-[12px] text-[var(--smoke)] mt-0.5 truncate">
                          {row.customer_name || "Guest"}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                          {_statusLabel(row.status)}
                        </span>
                        <ChevronRight size={16} className="text-[var(--smoke)]"/>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="px-4 py-2 text-[10px] text-[var(--smoke)] border-t border-gray-200 text-center">
          Tapping a sale opens its details in a new tab · Your current cart stays intact
        </div>
      </div>
    </div>
  );
}
